import { useCallback, useRef, useState } from 'react';
import { getCsrfToken } from '@/lib/utils.ts';
import { AiActionPreview, AiChatDisplayMessage, AiStreamEvent } from '@/types/AiChat';

interface SendMessageParams {
    schoolId: number;
    message: string;
    conversationId?: number | null;
}

interface UseAiChatStreamResult {
    messages: AiChatDisplayMessage[];
    isStreaming: boolean;
    error: string | null;
    conversationId: number | null;
    sendMessage: (params: SendMessageParams) => Promise<void>;
    startNewConversation: () => void;
    loadHistory: (conversationId: number, historyMessages: AiChatDisplayMessage[]) => void;
    dismissError: () => void;
}

/**
 * צריכת streaming (SSE) מה-endpoint POST /api/ai/chat/ — RTK Query אינו תומך native ב-SSE,
 * ולכן משתמשים כאן ב-fetch()+ReadableStream ישירות (ראו AI_BOT_IMPLEMENTATION_CHECKLIST.md).
 */
export function useAiChatStream(): UseAiChatStreamResult {
    const [messages, setMessages] = useState<AiChatDisplayMessage[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [conversationId, setConversationId] = useState<number | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const startNewConversation = useCallback(() => {
        abortRef.current?.abort();
        setMessages([]);
        setConversationId(null);
        setError(null);
        setIsStreaming(false);
    }, []);

    const loadHistory = useCallback((id: number, historyMessages: AiChatDisplayMessage[]) => {
        abortRef.current?.abort();
        setConversationId(id);
        setMessages(historyMessages);
        setError(null);
        setIsStreaming(false);
    }, []);

    const dismissError = useCallback(() => setError(null), []);

    const sendMessage = useCallback(async ({ schoolId, message, conversationId: convIdParam }: SendMessageParams) => {
        const activeConversationId = convIdParam ?? conversationId ?? null;

        setError(null);
        setIsStreaming(true);
        setMessages((prev) => [...prev, { role: 'user', text: message }, { role: 'model', text: '' }]);

        const applyEvent = (event: AiStreamEvent) => {
            switch (event.type) {
                case 'conversation':
                    if (typeof event.data.id === 'number') setConversationId(event.data.id);
                    break;
                case 'text_delta':
                    setMessages((prev) => {
                        const next = [...prev];
                        const last = next[next.length - 1];
                        if (last && last.role === 'model') {
                            next[next.length - 1] = { ...last, text: last.text + (event.data.text ?? '') };
                        }
                        return next;
                    });
                    break;
                case 'message_end':
                    setMessages((prev) => {
                        const next = [...prev];
                        const last = next[next.length - 1];
                        if (last && last.role === 'model') {
                            next[next.length - 1] = { ...last, citations: event.data.citations ?? [] };
                        }
                        return next;
                    });
                    break;
                case 'tool_call': {
                    if (event.data.name !== 'extensions_prepare_update') break;
                    const result = event.data.result as AiActionPreview | undefined;
                    if (!result || (result.status !== 'ready' && result.status !== 'no_changes')) break;
                    setMessages((prev) => {
                        const next = [...prev];
                        const last = next[next.length - 1];
                        if (last && last.role === 'model') {
                            next[next.length - 1] = {
                                ...last,
                                action: { preview: result, status: result.status },
                            };
                        }
                        return next;
                    });
                    break;
                }
                case 'error':
                    setError(event.data.message ?? 'שגיאה לא ידועה בעוזר ה-AI');
                    break;
                default:
                    break;
            }
        };

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const response = await fetch('/api/ai/chat/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken() },
                credentials: 'same-origin',
                body: JSON.stringify({ school_id: schoolId, conversation_id: activeConversationId, message }),
                signal: controller.signal,
            });

            if (!response.ok || !response.body) {
                setError('שגיאה בהתחברות לשרת עוזר ה-AI');
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';

            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                const chunks = buffer.split('\n\n');
                buffer = chunks.pop() ?? '';

                for (const chunk of chunks) {
                    const line = chunk.trim();
                    if (!line.startsWith('data:')) continue;
                    const jsonStr = line.slice('data:'.length).trim();
                    if (!jsonStr) continue;
                    try {
                        applyEvent(JSON.parse(jsonStr) as AiStreamEvent);
                    } catch {
                        // שורת SSE לא תקינה — מתעלמים ולא מפילים את כל ה-stream
                    }
                }
            }
        } catch (err) {
            if ((err as Error).name !== 'AbortError') {
                setError('אירעה שגיאה בתקשורת עם עוזר ה-AI');
            }
        } finally {
            setIsStreaming(false);
        }
    }, [conversationId]);

    return { messages, isStreaming, error, conversationId, sendMessage, startNewConversation, loadHistory, dismissError };
}
